import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { apiService } from '@/services/api';
import { Business } from '@saas-platform/shared-types';
import { AuthContext } from './AuthContext';

interface BusinessProfileContextType {
    business: Business | null;
    subscription: any | null;
    plan: any | null;
    isTrialing: boolean;
    isPaidActive: boolean;
    loading: boolean;
    error: string | null;
    refreshBusiness: () => Promise<void>;
    updateBusiness: (data: Partial<Business>) => Promise<void>;
    hasFeature: (featureKey: string) => boolean;
}

const BusinessProfileContext = createContext<BusinessProfileContextType | undefined>(undefined);

interface BusinessProfileProviderProps {
    children: ReactNode;
}

export const BusinessProfileProvider: React.FC<BusinessProfileProviderProps> = ({ children }) => {
    const [business, setBusiness] = useState<Business | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    // Use AuthContext directly so we don't throw if rendered outside AuthProvider (e.g. after ErrorBoundary unmount)
    const authContext = useContext(AuthContext);
    const isAuthenticated = authContext?.isAuthenticated ?? false;

    const fetchBusinessDetails = useCallback(async () => {
        if (!isAuthenticated) {
            setBusiness(null);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await apiService.getBusinessDetails();
            setBusiness(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load business profile');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchBusinessDetails();
    }, [fetchBusinessDetails]);

    useEffect(() => {
        const handleBillingRefresh = () => {
            void fetchBusinessDetails();
        };
        window.addEventListener('billing:refresh', handleBillingRefresh);
        return () => {
            window.removeEventListener('billing:refresh', handleBillingRefresh);
        };
    }, [fetchBusinessDetails]);

    const refreshBusiness = async () => {
        await fetchBusinessDetails();
    };

    const updateBusiness = async (data: Partial<Business>) => {
        try {
            setLoading(true);
            const updatedBusiness = await apiService.updateBusinessDetails(data);
            setBusiness(updatedBusiness);
        } catch (err: any) {
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const subscription = business?.billing?.subscription || null;
    const plan = subscription?.plan || null;
    const isTrialing = subscription?.status === 'TRIALING';
    const isPaidActive = subscription?.status === 'ACTIVE';

    const hasFeature = useCallback(
        (featureKey: string): boolean => {
            if (!business?.billing?.features) return true;
            return Boolean(business.billing.features[featureKey]);
        },
        [business?.billing?.features]
    );

    const value: BusinessProfileContextType = {
        business,
        subscription,
        plan,
        isTrialing,
        isPaidActive,
        loading,
        error,
        refreshBusiness,
        updateBusiness,
        hasFeature,
    };

    return (
        <BusinessProfileContext.Provider value={value}>
            {children}
        </BusinessProfileContext.Provider>
    );
};

export const useBusinessProfile = (): BusinessProfileContextType => {
    const context = useContext(BusinessProfileContext);
    if (context === undefined) {
        throw new Error('useBusinessProfile must be used within a BusinessProfileProvider');
    }
    return context;
};
